import { ReactNode } from 'react';

export interface QuickAccessAppProps {
    icon: ReactNode;
    iconwhite?: ReactNode;
    name: string;
    content: ReactNode;
    eplus: string;
    integrations?: Record<string, { show: boolean; img: string }>;
    link?: string;
    hide?: boolean;
    suportLink?: string;
    slug: string;
    tags?: string[];
}

export const ALPHABET_INDEX = ['#', ...'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('')];

export type ListFilter = 'all' | 'favorites';
export type LetterGroup = [string, QuickAccessAppProps[]];
export type SectionRefs = Record<string, HTMLDivElement | null>;

export const trackClick = (app: string) => {
    fetch(`https://tools.empreender.com.br/produtos-empreender-click?app=${app}&origem=CLICK_QUALQUER`, { method: 'POST' });
};

export const normalizeEplus = (key: string) => key.trim().toUpperCase();

export const normalizeAppKey = (value: string) =>
    value
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]/g, '');

export function getAppFirstLetter(name: string): string {
    const trimmed = name.trim();
    if (!trimmed) return '#';
    const ch = trimmed[0]!.toUpperCase();
    if (/[0-9]/.test(ch)) return '#';
    if (/[A-ZÀ-Ü]/.test(ch)) {
        return ch.normalize('NFD').replace(/\p{M}/gu, '');
    }
    return '#';
}

export function getEffectivePinnedKeys(userFixed: string[] | undefined, visible: QuickAccessAppProps[]) {
    if (userFixed === undefined) {
        return visible.map((a) => normalizeEplus(a.eplus));
    }
    return userFixed.map((k) => normalizeEplus(String(k)));
}

export function buildBarApps(visibleApps: QuickAccessAppProps[], pinnedList: string[] | undefined) {
    if (pinnedList === undefined) return visibleApps;
    if (pinnedList.length === 0) return [];
    const byEplus = new Map(visibleApps.map((a) => [normalizeEplus(a.eplus), a]));
    return pinnedList.map((key) => byEplus.get(normalizeEplus(String(key)))).filter(Boolean) as QuickAccessAppProps[];
}

export function filterDrawerApps(
    apps: QuickAccessAppProps[],
    listFilter: ListFilter,
    searchQuery: string,
    isPinned: (app: QuickAccessAppProps) => boolean,
) {
    let result = apps;
    if (listFilter === 'favorites') result = result.filter(isPinned);
    const q = searchQuery.trim().toLowerCase();
    if (q !== '') {
        result = result.filter((app) => {
            const nameMatch = app.name.toLowerCase().includes(q);
            const slugMatch = app.slug.toLowerCase().includes(q);
            const tagMatch = Array.isArray(app.tags) && app.tags.some((tag) => tag.toLowerCase().includes(q));
            return nameMatch || slugMatch || tagMatch;
        });
    }
    return result;
}

export function groupAppsByLetter(apps: QuickAccessAppProps[]): LetterGroup[] {
    const map = new Map<string, QuickAccessAppProps[]>();
    for (const app of apps) {
        const letter = getAppFirstLetter(app.name);
        const group = map.get(letter) ?? [];
        group.push(app);
        map.set(letter, group);
    }
    return [...map.entries()].sort(([a], [b]) => {
        if (a === '#') return -1;
        if (b === '#') return 1;
        return a.localeCompare(b, 'pt-BR');
    });
}

export function getAvailableLetters(apps: QuickAccessAppProps[]) {
    return new Set(apps.map((app) => getAppFirstLetter(app.name)));
}

export function getSectionScrollTop(targetEl: HTMLElement, container: HTMLElement) {
    return container.scrollTop + targetEl.getBoundingClientRect().top - container.getBoundingClientRect().top;
}

export function resolveActiveLetterFromScroll(
    container: HTMLDivElement,
    groupedByLetter: LetterGroup[],
    sectionRefs: SectionRefs,
    sectionHeaderRefs: SectionRefs,
): string | null {
    if (groupedByLetter.length === 0 || container.scrollTop < 8) {
        return null;
    }

    const maxScroll = container.scrollHeight - container.clientHeight;
    if (maxScroll > 0 && container.scrollTop >= maxScroll - 48) {
        return groupedByLetter[groupedByLetter.length - 1]![0];
    }

    const containerTop = container.getBoundingClientRect().top;
    const anchorY = 56;
    let current: string = groupedByLetter[0]![0];
    let topmostVisible: string | null = null;
    let topmostY = Infinity;

    for (const [letter] of groupedByLetter) {
        const header = sectionHeaderRefs[letter] ?? sectionRefs[letter];
        if (!header) continue;

        const top = header.getBoundingClientRect().top - containerTop;
        if (top <= anchorY) current = letter;
        if (top > -32 && top < topmostY) {
            topmostY = top;
            topmostVisible = letter;
        }
    }

    if (topmostVisible && topmostY > anchorY) {
        return topmostVisible;
    }

    return current;
}
